// The yaw convention (open question 3 in the engine suggestions, settled here for the controller): the kernel's
// `Transform`. Yaw is the angle about +Y in radians, right-handed, so it is three.js's `rotation.y`; yaw 0 faces +Z,
// which is also glTF's front. forward(yaw) = (sin yaw, cos yaw) on the XZ plane. A camera at yaw `c` looks along
// forward(c), and the screen's right is right(c) = (-cos c, sin c).

/** A direction or point on the ground plane. */
export interface XZ {
  x: number;
  z: number;
}

/** The unit vector a yaw faces. Yaw 0 faces +Z. */
export function forwardOf(yaw: number): XZ {
  return { x: Math.sin(yaw), z: Math.cos(yaw) };
}

/** The screen's right for a camera (or a body) at this yaw. */
export function rightOf(yaw: number): XZ {
  return { x: -Math.cos(yaw), z: Math.sin(yaw) };
}

/** The yaw that faces (x, z). 0 for a zero vector. */
export function yawOf(x: number, z: number): number {
  return x === 0 && z === 0 ? 0 : Math.atan2(x, z);
}

/** An angle wrapped to (-π, π]. */
export function wrapAngle(a: number): number {
  const w = Math.atan2(Math.sin(a), Math.cos(a));
  return w === -Math.PI ? Math.PI : w;
}

/** Turn `from` toward `to` by at most `maxStep` radians (the shorter way). */
export function turnToward(from: number, to: number, maxStep: number): number {
  const d = wrapAngle(to - from);
  return Math.abs(d) <= maxStep ? to : wrapAngle(from + Math.sign(d) * maxStep);
}

/**
 * A stick (x right, y up or forward, in the unit disc) to a world direction on the ground plane, relative to a camera
 * at `cameraYaw`: stick up walks where the camera looks. The length is kept (the tilt is the speed).
 */
export function stickToWorld(stick: { x: number; y: number }, cameraYaw: number): XZ {
  const f = forwardOf(cameraYaw);
  const r = rightOf(cameraYaw);
  return { x: f.x * stick.y + r.x * stick.x, z: f.z * stick.y + r.z * stick.x };
}
